import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { useTheme } from './contexts/ThemeContext';
import { BottomNavigation } from './components/BottomNavigation';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import pushNotificationService from './services/pushNotificationService';
const HomeScreen = lazy(() => import('./components/HomeScreen').then(m => ({ default: m.HomeScreen })));
const SearchScreen = lazy(() => import('./components/SearchScreen').then(m => ({ default: m.SearchScreen })));
const StreamsScreen = lazy(() => import('./components/StreamsScreen'));
const CirclesScreen = lazy(() => import('./components/CirclesScreen').then(m => ({ default: m.CirclesScreen })));
const RoomsScreen = lazy(() => import('./components/RoomsScreen').then(m => ({ default: m.RoomsScreen })));
const SimpleChallengesScreen = lazy(() => import('./components/SimpleChallengesScreen').then(m => ({ default: m.SimpleChallengesScreen })));
const SimpleCirclesScreen = lazy(() => import('./components/SimpleCirclesScreen').then(m => ({ default: m.SimpleCirclesScreen })));
const SimpleRoomsScreen = lazy(() => import('./components/SimpleRoomsScreen').then(m => ({ default: m.SimpleRoomsScreen })));
const CreateChallengeScreen = lazy(() => import('./components/CreateChallengeScreen').then(m => ({ default: m.CreateChallengeScreen })));
const CreateCircleScreen = lazy(() => import('./components/CreateCircleScreen').then(m => ({ default: m.CreateCircleScreen })));
const CreateRoomScreen = lazy(() => import('./components/CreateRoomScreen').then(m => ({ default: m.CreateRoomScreen })));
const VerificationSystem = lazy(() => import('./components/VerificationSystem').then(m => ({ default: m.VerificationSystem })));
const AdvancedVerificationSystem = lazy(() => import('./components/Verification/AdvancedVerificationSystem').then(m => ({ default: m.AdvancedVerificationSystem })));
const ProfileScreen = lazy(() => import('./components/ProfileScreen').then(m => ({ default: m.ProfileScreen })));
const ChatScreen = lazy(() => import('./components/ChatScreen').then(m => ({ default: m.ChatScreen })));
const ContentCreationScreen = lazy(() => import('./components/ContentCreationScreen').then(m => ({ default: m.ContentCreationScreen })));
const EnhancedProfileScreen = lazy(() => import('./components/EnhancedProfileScreen').then(m => ({ default: m.EnhancedProfileScreen })));
const RecommendationsScreen = lazy(() => import('./components/RecommendationsScreen').then(m => ({ default: m.RecommendationsScreen })));
const MonetizationScreen = lazy(() => import('./components/MonetizationScreen').then(m => ({ default: m.MonetizationScreen })));
const MLAnalyticsScreen = lazy(() => import('./components/MLAnalyticsScreen').then(m => ({ default: m.MLAnalyticsScreen })));
const TwilioTestScreen = lazy(() => import('./components/TwilioTestScreen').then(m => ({ default: m.TwilioTestScreen })));
const LiveStreamingScreen = lazy(() => import('./components/LiveStreamingScreen').then(m => ({ default: m.LiveStreamingScreen })));
const LiveRoomView = lazy(() => import('./components/LiveRoomView').then(m => ({ default: m.LiveRoomView })));
const NotificationsScreen = lazy(() => import('./components/NotificationsScreen').then(m => ({ default: m.NotificationsScreen })));
const VoiceAudioScreen = lazy(() => import('./components/VoiceAudioScreen').then(m => ({ default: m.VoiceAudioScreen })));
const WelcomeScreen = lazy(() => import('./components/WelcomeScreen').then(m => ({ default: m.WelcomeScreen })));
const SplashScreen = lazy(() => import('./components/SplashScreen').then(m => ({ default: m.SplashScreen })));
const PushNotificationTest = lazy(() => import('./components/PushNotificationTest').then(m => ({ default: m.PushNotificationTest })));
const LandingScreen = lazy(() => import('./components/auth/LandingScreen').then(m => ({ default: m.LandingScreen })));
const LoginScreen = lazy(() => import('./components/auth/LoginScreen').then(m => ({ default: m.LoginScreen })));
const RegisterScreen = lazy(() => import('./components/auth/RegisterScreen').then(m => ({ default: m.RegisterScreen })));
const ForgotPasswordScreen = lazy(() => import('./components/auth/ForgotPasswordScreen').then(m => ({ default: m.ForgotPasswordScreen })));
const FacialVerificationScreen = lazy(() => import('./components/auth/FacialVerificationScreen').then(m => ({ default: m.FacialVerificationScreen })));
const SignupScreen = lazy(() => import('./components/auth/SignupScreen').then(m => ({ default: m.SignupScreen })));
const EntityRegistrationScreen = lazy(() => import('./components/auth/EntityRegistrationScreen').then(m => ({ default: m.EntityRegistrationScreen })));
const SilentChairsScreen = lazy(() => import('./components/community/SilentChairsScreen').then(m => ({ default: m.SilentChairsScreen })));
const WorkshopModeScreen = lazy(() => import('./components/community/WorkshopModeScreen').then(m => ({ default: m.WorkshopModeScreen })));
const PostDetail = lazy(() => import('./components/PostDetail').then(m => ({ default: m.PostDetail })));
const ProtectedAnalyticsDashboard = lazy(() => import('./components/ProtectedAnalyticsDashboard').then(m => ({ default: m.ProtectedAnalyticsDashboard })));
const EditProfileScreen = lazy(() => import('./components/EditProfileScreen').then(m => ({ default: m.EditProfileScreen })));
import './index.css';


function AppContent() {
  const { backgroundColor } = useTheme();
  const location = useLocation();
  
  // Hide bottom navigation on specific screens
  const hideBottomNav = location.pathname.startsWith('/post/') || 
                       location.pathname === '/create' ||
                       location.pathname === '/landing' ||
                       location.pathname === '/signup' ||
                       location.pathname === '/entity-registration' ||
                       location.pathname === '/welcome' ||
                       location.pathname === '/' || // Hide on splash screen
                       location.pathname === '/login' || // Hide on login screen
                       location.pathname === '/register' || // NEW
                       location.pathname === '/forgot-password' || // NEW
                       location.pathname === '/facial-verification' || // NEW
                       location.pathname === '/profile/edit'; // NEW
  
  return (
    <div className="App min-h-screen max-w-md mx-auto bg-white shadow-lg" style={{ backgroundColor }}>
      <Suspense fallback={<div className="p-4 text-center text-gray-500">Loading...</div>}>
      <Routes>
        <Route path="/landing" element={<LandingScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/register" element={<RegisterScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/facial-verification" element={<FacialVerificationScreen />} />
        <Route path="/signup" element={<SignupScreen />} />
        <Route path="/entity-registration" element={<EntityRegistrationScreen />} />
        <Route path="/silent-chairs" element={<SilentChairsScreen />} />
        <Route path="/workshop-mode" element={<WorkshopModeScreen />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/" element={<SplashScreen />} />
        <Route path="/home" element={
          <ProtectedRoute>
            <HomeScreen />
          </ProtectedRoute>
        } />
        <Route path="/search" element={<SearchScreen />} />
              <Route path="/streams" element={<StreamsScreen />} />
              <Route path="/circles" element={
                <ErrorBoundary fallback={<SimpleCirclesScreen />}>
                  <CirclesScreen />
                </ErrorBoundary>
              } />
              <Route path="/rooms" element={
                <ErrorBoundary fallback={<SimpleRoomsScreen />}>
                  <RoomsScreen />
                </ErrorBoundary>
              } />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/profile/:username" element={<ProfileScreen />} />
        <Route path="/profile/edit" element={<EditProfileScreen />} />
        <Route path="/chat" element={<ChatScreen />} />
        <Route path="/create" element={<ContentCreationScreen />} />
        <Route path="/create-challenge" element={<CreateChallengeScreen />} />
        <Route path="/create-circle" element={<CreateCircleScreen />} />
        <Route path="/create-room" element={<CreateRoomScreen />} />
        <Route path="/enhanced-profile" element={<EnhancedProfileScreen />} />
        <Route path="/verification" element={<VerificationSystem />} />
        <Route path="/verification/advanced" element={<AdvancedVerificationSystem />} />
        <Route path="/recommendations" element={<RecommendationsScreen />} />
        <Route path="/monetization" element={<MonetizationScreen />} />
        <Route path="/ml-analytics" element={<MLAnalyticsScreen />} />
        <Route path="/twilio-test" element={<TwilioTestScreen />} />
        <Route path="/live-streaming" element={<LiveStreamingScreen />} />
        <Route path="/room/:id/live" element={<LiveRoomView />} />
        <Route path="/notifications" element={<NotificationsScreen />} />
        <Route path="/voice-audio" element={<VoiceAudioScreen />} />
        <Route path="/push-test" element={<PushNotificationTest />} />
        <Route path="/post/:id" element={<PostDetail />} />
        <Route path="/dashboard" element={<ProtectedAnalyticsDashboard userId="demo-user" />} />
        <Route path="*" element={
          <ProtectedRoute>
            <HomeScreen />
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>
      {!hideBottomNav && <BottomNavigation />}
    </div>
  );
}

function App() {
  const notificationListener = useRef<any>();
  const responseListener = useRef<any>();
  const [expoPushToken, setExpoPushToken] = useState('');

  useEffect(() => {
    async function registerPushNotifications() {
      const token = await pushNotificationService.registerForPushNotifications();
      setExpoPushToken(token ?? '');
      
      if (token) {
        // Send token to your backend
        const userId = localStorage.getItem('userId') || 'demo-user';
        await pushNotificationService.sendTokenToServer(userId, token);
      }
    }

    registerPushNotifications();

    const receivedListener = pushNotificationService.addNotificationReceivedListener(
      (notification) => {
        console.log('📬 Notification received:', notification);
        // Show in-app notification banner
      }
    );

    const tapListener = pushNotificationService.addNotificationResponseListener(
      (response) => {
        console.log('👆 Notification tapped:', response);
        const screen = response.notification?.request?.content?.data?.screen;
        // Navigate to specific screen if needed
        // navigation.navigate(screen);
      }
    );

    // Store listeners for cleanup
    if (receivedListener && typeof receivedListener === 'object') {
      notificationListener.current = receivedListener;
    }
    if (tapListener && typeof tapListener === 'object') {
      responseListener.current = tapListener;
    }

    return () => {
      if (notificationListener.current?.remove) {
        notificationListener.current.remove();
      }
      if (responseListener.current?.remove) {
        responseListener.current.remove();
      }
    };
  }, []);

  return (
    <AuthProvider>
      <SocketProvider>
        <UserProvider>
          <Router>
            <AppContent />
          </Router>
        </UserProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
